import type { QuestionDemographicDimension } from './question-dashboard'

/** Human-readable labels for demographic segment keys — aligned with FE audience catalog. */
export const GENDER_DISPLAY_LABELS: Record<string, string> = {
  female: 'Female',
  male: 'Male',
  non_binary: 'Non-binary',
  prefer_not_to_say: 'Prefer not to say',
}

export const EDUCATION_DISPLAY_LABELS: Record<string, string> = {
  high_school: 'High School',
  some_college: 'Some College',
  bachelors: "Bachelor's Degree",
  masters: "Master's Degree",
  doctorate: 'Doctorate',
  trade: 'Trade or Vocational',
}

export const EMPLOYMENT_DISPLAY_LABELS: Record<string, string> = {
  full_time: 'Employed Full-time',
  part_time: 'Employed Part-time',
  self_employed: 'Self-employed',
  student: 'Student',
  unemployed: 'Unemployed',
  retired: 'Retired',
}

export const MARITAL_STATUS_DISPLAY_LABELS: Record<string, string> = {
  single: 'Single',
  married: 'Married',
  divorced: 'Divorced',
  widowed: 'Widowed',
  domestic_partnership: 'Domestic Partnership',
}

export const HOUSEHOLD_SIZE_DISPLAY_LABELS: Record<string, string> = {
  '1': '1 — Just Me',
  '2': '2 People',
  '3': '3 People',
  '4': '4 People',
  '5': '5 People',
  '6': '6 People',
  '7_plus': '7+ People',
}

export const NUMBER_OF_CHILDREN_DISPLAY_LABELS: Record<string, string> = {
  none: 'None',
  '1': '1',
  '2': '2',
  '3': '3',
  '4': '4',
  '5_plus': '5+',
}

export const HOUSING_STATUS_DISPLAY_LABELS: Record<string, string> = {
  rent: 'Rent',
  own: 'Own',
  other: 'Other',
}

export const INDUSTRY_DISPLAY_LABELS: Record<string, string> = {
  technology: 'Technology/IT',
  healthcare: 'Healthcare/Medical',
  finance: 'Finance/Banking',
  education: 'Education',
  retail: 'Retail/E-commerce',
  manufacturing: 'Manufacturing',
  construction: 'Construction',
  transportation: 'Transportation/Logistics',
  energy: 'Energy/Utilities',
  telecom: 'Telecommunications',
  government: 'Government/Public Sector',
  legal: 'Legal Services',
  media: 'Media/Entertainment',
  hospitality: 'Hospitality/Tourism',
  real_estate: 'Real Estate',
  agriculture: 'Agriculture/Farming',
  insurance: 'Insurance',
  consulting: 'Consulting/Professional Services',
  marketing: 'Marketing/Advertising',
  nonprofit: 'Nonprofit/NGO',
  science: 'Science/Research',
  automotive: 'Automotive',
  aerospace: 'Aerospace/Aviation',
  pharma: 'Pharmaceuticals/Biotechnology',
  food_beverage: 'Food & Beverage',
  arts: 'Arts/Design',
  hr: 'Human Resources',
  customer_service: 'Customer Service',
  sales: 'Sales',
  military: 'Military/Defense',
  self_employed_ind: 'Self-employed',
  student_ind: 'Student',
  unemployed_ind: 'Unemployed',
  other_ind: 'Other',
}

export const US_STATE_DISPLAY_LABELS: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
}

export const DEVICE_TYPE_DISPLAY_LABELS: Record<string, string> = {
  mobile: 'Mobile',
  web: 'Web',
  both: 'Mobile & Web',
}

export const AGE_BUCKET_DISPLAY_LABELS: Record<string, string> = {
  '18-24': '18-24',
  '25-34': '25-34',
  '35-44': '35-44',
  '45-54': '45-54',
  '55-64': '55-64',
  '65_plus': '65+',
}

const DIMENSION_LABEL_MAPS: Partial<
  Record<QuestionDemographicDimension, Record<string, string>>
> = {
  age: AGE_BUCKET_DISPLAY_LABELS,
  gender: GENDER_DISPLAY_LABELS,
  region: US_STATE_DISPLAY_LABELS,
  education: EDUCATION_DISPLAY_LABELS,
  employment: EMPLOYMENT_DISPLAY_LABELS,
  deviceType: DEVICE_TYPE_DISPLAY_LABELS,
  maritalStatus: MARITAL_STATUS_DISPLAY_LABELS,
  householdSize: HOUSEHOLD_SIZE_DISPLAY_LABELS,
  numberOfChildren: NUMBER_OF_CHILDREN_DISPLAY_LABELS,
  industry: INDUSTRY_DISPLAY_LABELS,
  housingStatus: HOUSING_STATUS_DISPLAY_LABELS,
}

/** Resolves a raw demographic segment key to a display label for charts and tooltips. */
export function resolveDemographicSegmentDisplayLabel(
  dimension: QuestionDemographicDimension,
  segmentKey: string,
): string {
  const key = segmentKey.trim()
  if (!key) return key
  const map = DIMENSION_LABEL_MAPS[dimension]
  return map?.[key] ?? key.replaceAll('_', ' ')
}
